import {
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  signal,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { FormBuilderBase } from '@shared/components/row-edition-form/form-builder/form-builder.model';

import { HighlightedMatchComponent } from './highlighted-match.component';

/**
 * Single-value input with autocompletion — a `.gx-combo` box over a `.g-menu`
 * suggestion panel, with the typed slice highlighted in each result.
 *
 * Unrelated to `app-entity-auto-complete`, which renders no list of its own and
 * delegates to a feature-layer entity picker dialog.
 */
@Component({
  selector: 'app-autocomplete',
  imports: [DropdownComponent, TranslatePipe, HighlightedMatchComponent],
  templateUrl: './autocomplete.component.html',
  styles: `
    :host {
      display: block;
      width: 100%;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ]
})
export class AutocompleteComponent implements ControlValueAccessor {
  id = input<FormBuilderBase['id']>();
  options = input<readonly string[] | undefined>([]);
  /** When false, typing a value the list does not contain commits nothing. */
  allowCustomValues = input<boolean>(true);
  placeholder = input<string>();
  disabled = input<boolean>(false);

  protected readonly ICONS = ICONS;
  protected readonly wrapper = viewChild.required<ElementRef<HTMLElement>>('wrapperEl');

  /** What the box shows: the committed value until the user starts typing. */
  protected readonly searchText = signal('');
  protected readonly highlightedIndex = signal(-1);
  protected readonly isOpen = signal(false);
  protected readonly wrapperWidth = signal(0);
  private readonly isDisabledByForm = signal(false);

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  protected readonly isEffectivelyDisabled = computed(
    () => this.disabled() || this.isDisabledByForm()
  );

  protected readonly filteredOptions = computed<string[]>(() => {
    const search = foldForSearch(this.searchText()).trim();
    const options = this.options() ?? [];
    if (search.length === 0) return [...options];
    return options.filter((option) => foldForSearch(option).includes(search));
  });

  writeValue(value: string | null): void {
    this.searchText.set(value ?? '');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByForm.set(isDisabled);
  }

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
    if (this.allowCustomValues()) {
      this.onChange(this.currentText());
    }
    this.refreshPanel();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    const options = this.filteredOptions();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (options.length > 0) {
          this.highlightedIndex.update((index) => (index + 1) % options.length);
        }
        return;

      case 'ArrowUp':
        event.preventDefault();
        if (options.length > 0) {
          this.highlightedIndex.update((index) =>
            index <= 0 ? options.length - 1 : index - 1
          );
        }
        return;

      case 'Enter': {
        event.preventDefault();
        const index = this.highlightedIndex();
        if (index >= 0 && index < options.length) {
          this.selectOption(options[index]);
        } else if (this.allowCustomValues()) {
          this.commit(this.currentText());
        }
        return;
      }

      case 'Escape':
        this.close();
        this.onTouched();
        return;
    }
  }

  protected onInputFocus(): void {
    this.refreshPanel();
  }

  protected selectOption(option: string): void {
    this.searchText.set(option);
    this.commit(option);
  }

  protected clear(): void {
    if (this.isEffectivelyDisabled()) return;
    this.searchText.set('');
    this.commit(null);
  }

  protected close = (): void => {
    this.isOpen.set(false);
    this.highlightedIndex.set(-1);
  };

  private currentText(): string | null {
    const text = this.searchText().trim();
    return text.length > 0 ? text : null;
  }

  private commit(value: string | null): void {
    this.onChange(value);
    this.close();
  }

  private refreshPanel(): void {
    if (this.isEffectivelyDisabled()) return;

    const hasSuggestions = this.filteredOptions().length > 0;
    if (hasSuggestions && !this.isOpen()) {
      this.wrapperWidth.set(this.wrapper().nativeElement.offsetWidth);
    }
    this.isOpen.set(hasSuggestions);
    this.highlightedIndex.set(hasSuggestions ? 0 : -1);
  }
}
